import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import {
  COUNTERSIGN_DOCUMENT_KINDS,
  RESELLER_APPLICATION_DOCUMENT_KINDS,
  RESELLER_APPLICATION_DOCUMENT_LABELS,
  isOpenApplication,
  isPdfFile,
  type CountersignDocumentKind,
  type ResellerApplication,
  type ResellerApplicationDocumentKind,
} from "@xop/dashboard-shared";
import { Download, Eye, FileText, Upload } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { PageLoader } from "@/components/ui/page-loader";
import { PdfReader } from "@/components/pdf/pdf-reader";
import { toast } from "@/components/ui/use-toast";
import { ApiError } from "@/lib/api";
import { formatDateTime } from "./format.js";
import { downloadApplicationDocument, fetchApplicationDocumentBytes } from "./useResellerApplications.js";

// `@mui/utils` is only a transitive dependency of `@mui/material` here, not
// hoisted for direct import from apps/web — so the visually-hidden style is
// inlined rather than imported from `@mui/utils/visuallyHidden`. Keeps the
// `<input type=file>` focusable and testable (userEvent.upload / Cypress
// selectFile both reach it) while it stays invisible.
const VISUALLY_HIDDEN = {
  position: "absolute",
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

/**
 * The four document slots (spec §11.1): the applicant's two signed PDFs
 * (view inline, download) and XOP's two countersigned copies (same, plus an
 * upload zone while the application is open). Bytes always come through the
 * BFF (`fetchApplicationDocumentBytes`) and render via the shared `PdfReader`
 * — pdf.js to a canvas, never an iframe (the desktop shell hangs on one).
 *
 * The countersigned pair is what gates Activate, so this card is the one
 * place "why can't I activate yet" is answered with an action.
 */
export function DocumentsCard({
  application,
  canManage,
  isUploading,
  onUpload,
}: {
  application: ResellerApplication;
  canManage: boolean;
  isUploading: boolean;
  onUpload: (files: Partial<Record<CountersignDocumentKind, File>>) => Promise<void>;
}): JSX.Element {
  const [viewing, setViewing] = useState<ResellerApplicationDocumentKind | null>(null);
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<CountersignDocumentKind, string>>>({});
  const open = isOpenApplication(application);
  const isCountersign = (kind: ResellerApplicationDocumentKind): kind is CountersignDocumentKind =>
    (COUNTERSIGN_DOCUMENT_KINDS as readonly string[]).includes(kind);

  const handleFile = (kind: CountersignDocumentKind, file: File | undefined): void => {
    if (!file) return;
    // Mirror crm-api's checks so a wrong file never leaves the browser;
    // crm-api still verifies the %PDF- magic bytes.
    const error = isPdfFile(file);
    setUploadErrors((prev) => ({ ...prev, [kind]: error ?? undefined }));
    if (error) return;
    void onUpload({ [kind]: file }).catch((cause: unknown) => {
      // Only crm-api can answer the questions the browser cannot: do the
      // bytes actually start with `%PDF-`? Its refusal arrives as an
      // ApiError long after the click, and it belongs on the slot the
      // reviewer used — beside the client-side messages — not only in a
      // toast that fades. The slot itself stays as it was: nothing was
      // attached upstream.
      setUploadErrors((prev) => ({
        ...prev,
        [kind]: cause instanceof ApiError ? cause.message : "Couldn't upload the document.",
      }));
    });
  };

  const handleDownload = async (kind: ResellerApplicationDocumentKind, filename: string): Promise<void> => {
    try {
      await downloadApplicationDocument(application.id, kind, filename);
    } catch {
      toast({ title: "Couldn't download the document", description: "The CRM was unreachable.", severity: "error" });
    }
  };

  return (
    <Card data-cy="application-documents">
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
            <Box sx={{ color: "text.secondary", display: "flex" }}><FileText size={20} /></Box>
            <span>Documents</span>
          </Box>
        }
        subheader={
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Both countersigned copies must be uploaded before the application can be activated.
          </Typography>
        }
      />
      <CardContent sx={{ display: "grid", gap: 1.5 }}>
        {RESELLER_APPLICATION_DOCUMENT_KINDS.map((kind) => {
          const doc = application.documents[kind];
          const countersign = isCountersign(kind);
          const error = countersign ? uploadErrors[kind] : undefined;
          return (
            <Box
              key={kind}
              data-testid={`document-slot-${kind}`}
              data-cy={`document-slot-${kind}`}
              data-attached={doc.attached ? "true" : "false"}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "surfaceSoft",
                border: 1,
                borderColor: doc.attached ? "transparent" : (t) => alpha(t.palette.warning.main, 0.5),
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{RESELLER_APPLICATION_DOCUMENT_LABELS[kind]}</Typography>
                {doc.attached ? (
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    <span>{doc.filename}</span> · uploaded {formatDateTime(doc.uploaded_at)}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 12, color: "warning.main" }}>Not uploaded yet</Typography>
                )}
                {error && (
                  <Typography role="alert" data-cy={`upload-error-${kind}`} sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                    {error}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                {doc.attached && (
                  <>
                    <Button size="small" variant="outlined" color="inherit" startIcon={<Eye size={14} />} onClick={() => setViewing(kind)} data-cy={`document-view-${kind}`}>
                      View
                    </Button>
                    <Button size="small" variant="outlined" color="inherit" startIcon={<Download size={14} />} onClick={() => void handleDownload(kind, doc.filename)} data-cy={`document-download-${kind}`}>
                      Download
                    </Button>
                  </>
                )}
                {countersign && open && canManage && (
                  <Button size="small" variant="contained" component="label" startIcon={<Upload size={14} />} disabled={isUploading}>
                    {doc.attached ? "Replace" : "Upload"}
                    {/* Visually hidden rather than display:none so the input
                        stays focusable and testable (userEvent.upload / Cypress
                        selectFile both reach it). No `accept` filter: the
                        browser's own file-type filtering would silently drop a
                        non-PDF pick before it ever reaches `isPdfFile` below —
                        the message the reviewer needs ("Only a PDF can be
                        uploaded here") has to come from OUR check, not a
                        native picker that just hides the wrong files. */}
                    <Box
                      component="input"
                      type="file"
                      sx={VISUALLY_HIDDEN}
                      data-cy={`upload-${kind}`}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        handleFile(kind, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </CardContent>

      {viewing && (
        <DocumentViewerDialog
          applicationId={application.id}
          kind={viewing}
          filename={application.documents[viewing].attached ? (application.documents[viewing] as { filename: string }).filename : `${viewing}.pdf`}
          onClose={() => setViewing(null)}
          onDownload={(filename) => void handleDownload(viewing, filename)}
        />
      )}
    </Card>
  );
}

function DocumentViewerDialog({
  applicationId,
  kind,
  filename,
  onClose,
  onDownload,
}: {
  applicationId: number;
  kind: ResellerApplicationDocumentKind;
  filename: string;
  onClose: () => void;
  onDownload: (filename: string) => void;
}): JSX.Element {
  const bytes = useQuery({
    queryKey: ["admin", "reseller-application-document", applicationId, kind],
    queryFn: () => fetchApplicationDocumentBytes(applicationId, kind),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" data-cy="document-viewer">
      <DialogTitle>{RESELLER_APPLICATION_DOCUMENT_LABELS[kind]}</DialogTitle>
      <DialogContent>
        {bytes.isLoading && <PageLoader />}
        {bytes.isError && (
          <Typography role="alert" sx={{ fontSize: 14, color: "error.main" }}>
            This PDF couldn&rsquo;t be loaded. Use Download to view it.
          </Typography>
        )}
        {bytes.data && <PdfReader bytes={bytes.data} filename={filename} maxHeight="70vh" />}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" startIcon={<Download size={14} />} onClick={() => onDownload(filename)}>
          Download
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
